const collabDraftFetchTimestamps = new Map<string, number>();

// TTL for CollabDraftQuery cached data - 30 seconds
// This prevents stale data during SPA transitions while still allowing
// cache-first for SSR-rendered pages within the TTL window
const COLLAB_DRAFT_TTL_MS = 60 * 1000;

/**
 * Creates a composite cache key that includes both contentId and draftShareId.
 * This aligns with how Apollo caches queries by their variables.
 */
const getCacheKey = (contentId: string, draftShareId?: string): string => {
	return `${contentId}:${draftShareId || ''}`;
};

/**
 * Checks if the cached CollabDraftQuery data is stale (older than TTL).
 */
export function isCollabDraftCacheStale(contentId: string, draftShareId?: string): boolean {
	const key = getCacheKey(contentId, draftShareId);
	const lastFetchTime = collabDraftFetchTimestamps.get(key);
	if (!lastFetchTime) return true;
	return Date.now() - lastFetchTime > COLLAB_DRAFT_TTL_MS;
}

/**
 * Records the fetch timestamp for a CollabDraftQuery.
 * Call this after a successful network fetch.
 */
export function markCollabDraftFetched(contentId: string, draftShareId?: string): void {
	const key = getCacheKey(contentId, draftShareId);
	collabDraftFetchTimestamps.set(key, Date.now());
}

/**
 * Clears the fetch timestamp for a specific content.
 * Useful when you want to force a refetch on next access.
 */
export function clearCollabDraftTimestamp(contentId: string, draftShareId?: string): void {
	const key = getCacheKey(contentId, draftShareId);
	collabDraftFetchTimestamps.delete(key);
}

/**
 * Clears all fetch timestamps.
 * Useful for testing or when you want to invalidate all cached drafts.
 */
export function clearAllCollabDraftTimestamps(): void {
	collabDraftFetchTimestamps.clear();
}
